import * as React from "react";
import { useMemo } from "react";
import { ListBase, ListProps, useDataProvider, useListContext, useNotify, usePermissions } from "react-admin";
import { Button } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import { Link } from "react-router-dom";
import { FilterField } from "@src/components/Common/DataList";
import { NetOrcaTableListV1, TableListColumn } from "@src/components/Common/NetOrcaTableList";
import { LiveTableWrapper } from "@src/components/Common/tableUtils";
import { ExtendedDataProvider } from "@src/types";
import PackDashboardTabs from "../Pack/PackDashboard/PackDashboardTabs";
import AIProcessorRow, { prettyActionType } from "./AIProcessorRow";
import "./AIProcessors.css";

const resource = "serviceowner_ai_processors";
const resourcePack = "serviceowner_pack";
const title = "AI Processor";

const stageOptions = ['config', 'verify', 'execution', 'change_instance_validator']
    .map((stage) => ({ value: stage, label: prettyActionType(stage) }));

const AIProcessorListView = () => {
    const { refetch } = useListContext();
    const dataProvider: ExtendedDataProvider = useDataProvider();
    const notify = useNotify();
    const { permissions } = usePermissions();
    const isAdmin = permissions === "ADMIN";

    const triggerAIProcessor = (record: any) => {
        dataProvider.createAction(resourcePack, {
            data: undefined,
            verb: `trigger/service/${record?.service?.id}/${record.action_type}/`,
        })
            .then(() => notify('AI Processor is triggered'))
            .catch((error: any) => notify(JSON.stringify(error.body), 'error'));
    };

    const enableAIProcessor = (record: any, checked: boolean) => {
        dataProvider.update(resource, {
            id: record.id,
            data: { active: checked },
            previousData: { id: record.id },
        })
            .then(() => notify(`${title} ${checked ? 'Enabled' : 'Disabled'}`))
            .catch((error: any) => {
                if (error.status == 400) notify(`Error updating ${title}`, 'error');
            });
    };

    // Every one of these has a column of its own, so they all sit on the header
    // and the list needs no filter panel at all.
    const filterFields: FilterField[] = useMemo(() => [
        {
            name: 'llm_model_id',
            label: 'LLM Model',
            type: 'select',
            resource: 'llm_models',
            // that endpoint has no /filters/ route, so the options come from the
            // list itself
            optionsFrom: 'list',
            dataCy: 'llm-model-selector',
        },
        {
            name: 'service_id',
            label: 'Service',
            type: 'select',
            resource: 'services',
            dataCy: 'service-selector',
        },
        {
            name: 'action_type',
            label: 'Stage',
            type: 'select',
            options: stageOptions,
            dataCy: 'stage-filter',
        },
    ], []);

    const columns: TableListColumn[] = useMemo(() => [
        { label: 'Name' },
        { label: 'LLM Model', filters: ['llm_model_id'], dataCy: 'column-llm-model' },
        { label: 'Service', filters: ['service_id'], dataCy: 'column-service' },
        { label: 'Stage', filters: ['action_type'], dataCy: 'column-stage' },
        // the last track is the create button's, over each row's controls
    ], []);

    return (
        <NetOrcaTableListV1
            className="netorca-ap"
            dataCy="ai-processor-result-list"
            fields={filterFields}
            columns={columns}
            template="var(--ap-cols)"
            banner={<PackDashboardTabs />}
            emptyText="No AI processors found"
            noun="processor"
            // creating one belongs with the list, not among the filters
            headerTrailing={isAdmin && (
                <Link to={`/${resource}/create`}>
                    <Button className="ap-create" type="primary" size="small" icon={<PlusOutlined />}>
                        Create new AI processor
                    </Button>
                </Link>
            )}
            renderWrapper={(results) => <LiveTableWrapper refetch={refetch}>{results}</LiveTableWrapper>}
            renderRow={(record) => (
                <AIProcessorRow
                    key={record.id}
                    record={record}
                    detailPath={`/${resource}`}
                    isAdmin={isAdmin}
                    onToggleActive={enableAIProcessor}
                    onTrigger={triggerAIProcessor}
                />
            )}
        />
    );
};

const AIProcessorList = (props: ListProps) => (
    // page size is the batch the infinite scroll fetches
    <ListBase perPage={25} {...props}>
        <AIProcessorListView />
    </ListBase>
);

export default AIProcessorList;
